import { useState, useEffect } from 'react';
import { Save, RefreshCw, Plus, Trash2, Copy, Eye, EyeOff } from 'lucide-react';
import api from '../services/api';

export default function OrderSettings() {
  const [settings, setSettings] = useState({});
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showPin, setShowPin] = useState(false);
  
  // New discount form
  const [newCode, setNewCode] = useState('');
  const [newType, setNewType] = useState('percent');
  const [newValue, setNewValue] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, discountsRes] = await Promise.all([
        api.get('/order-admin/settings'),
        api.get('/order-admin/discounts')
      ]);
      if (settingsRes.data.success) setSettings(settingsRes.data.settings);
      if (discountsRes.data.success) setDiscounts(discountsRes.data.discounts);
    } catch (e) {
      setMessage('Veri yüklenemedi');
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await api.post('/order-admin/settings', settings);
      if (res.data.success) {
        setMessage('Ayarlar kaydedildi!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (e) {
      setMessage('Kayıt hatası');
    }
    setSaving(false);
  };

  const addDiscount = async () => {
    if (!newValue) return;
    try {
      const res = await api.post('/order-admin/discounts', {
        code: newCode,
        discount_type: newType,
        discount_value: parseFloat(newValue)
      });
      if (res.data.success) {
        setNewCode('');
        setNewValue('');
        fetchData();
      }
    } catch (e) {}
  };

  const deleteDiscount = async (id) => {
    if (!window.confirm('Bu kodu silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/order-admin/discounts/${id}`);
      fetchData();
    } catch (e) {}
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.origin + '/order');
    setMessage('Link kopyalandı!');
    setTimeout(() => setMessage(''), 2000);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Sipariş Ayarları</h1>
        <button
          onClick={copyLink}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
        >
          <Copy className="w-4 h-4" /> Sipariş Linki
        </button>
      </div>

      {message && (
        <div className="mb-4 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-cyan-400 text-sm">
          {message}
        </div>
      )}

      {/* General Settings */}
      <div className="bg-gray-800 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Genel Ayarlar</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-gray-400 text-sm mb-1 block">PIN Kodu</label>
            <div className="flex gap-2">
              <input
                type={showPin ? 'text' : 'password'}
                value={settings.pin_code || ''}
                onChange={(e) => setSettings({...settings, pin_code: e.target.value})}
                className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                maxLength={4}
              />
              <button
                onClick={() => setShowPin(!showPin)}
                className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
              >
                {showPin ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-1 block">Birim Fiyat ($)</label>
            <input
              type="number"
              step="0.5"
              value={settings.unit_price || ''}
              onChange={(e) => setSettings({...settings, unit_price: e.target.value})}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-1 block">Min Sipariş</label>
            <input
              type="number"
              value={settings.min_order || ''}
              onChange={(e) => setSettings({...settings, min_order: e.target.value})}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-1 block">Max Sipariş</label>
            <input
              type="number"
              value={settings.max_order || ''}
              onChange={(e) => setSettings({...settings, max_order: e.target.value})}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-1 block">🎁 Hediye Oranı (%)</label>
            <input
              type="number"
              value={settings.bonus_rate || '10'}
              onChange={(e) => setSettings({...settings, bonus_rate: e.target.value})}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              min="0"
              max="100"
            />
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-1 block">Ödeme Süresi (dk)</label>
            <input
              type="number"
              value={settings.payment_timeout || ''}
              onChange={(e) => setSettings({...settings, payment_timeout: e.target.value})}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            />
          </div>
        </div>
      </div>

      {/* Wallet & Telegram */}
      <div className="bg-gray-800 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Ödeme & Telegram</h2>
        
        <div className="space-y-4">
          <div>
            <label className="text-gray-400 text-sm mb-1 block">USDT TRC20 Cüzdan</label>
            <input
              type="text"
              value={settings.usdt_wallet || ''}
              onChange={(e) => setSettings({...settings, usdt_wallet: e.target.value})}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white font-mono text-sm"
              placeholder="TRC20 adresi"
            />
          </div>

	<div>
            <label className="text-gray-400 text-sm mb-1 block">TronGrid API Key</label>
            <input
              type="text"
              value={settings.trongrid_api_key || ''}
              onChange={(e) => setSettings({...settings, trongrid_api_key: e.target.value})}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white font-mono text-sm"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            />
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-1 block">Telegram Destek</label>
            <input
              type="text"
              value={settings.telegram_support || ''}
              onChange={(e) => setSettings({...settings, telegram_support: e.target.value})}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              placeholder="@username"
            />
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-1 block">Telegram Bot Token</label>
            <input
              type="text"
              value={settings.telegram_bot_token || ''}
              onChange={(e) => setSettings({...settings, telegram_bot_token: e.target.value})}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white font-mono text-sm"
              placeholder="123456:ABC-DEF..."
            />
          </div>

          <div>
            <label className="text-gray-400 text-sm mb-1 block">Telegram Chat ID</label>
            <input
              type="text"
              value={settings.telegram_chat_id || ''}
              onChange={(e) => setSettings({...settings, telegram_chat_id: e.target.value})}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              placeholder="-100123456789"
            />
          </div>
        </div>
      </div>

      <button
        onClick={saveSettings}
        disabled={saving}
        className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
      >
        {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
        Kaydet
      </button>

      {/* Discount Codes */}
      <div className="bg-gray-800 rounded-xl p-6 mt-6">
        <h2 className="text-lg font-semibold text-white mb-4">İndirim Kodları</h2>
        
        {/* Add new */}
        <div className="flex flex-wrap gap-2 mb-4">
          <input
            type="text"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value.toUpperCase())}
            placeholder="Kod (boş=otomatik)"
            className="flex-1 min-w-[120px] px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
          />
          <select
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
          >
            <option value="percent">% İndirim</option>
            <option value="fixed_price">Sabit Fiyat</option>
          </select>
          <input
            type="number"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder={newType === 'percent' ? '% oran' : '$ fiyat'}
            className="w-24 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
          />
          <button
            onClick={addDiscount}
            className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Ekle
          </button>
        </div>

        {/* List */}
        <div className="space-y-2">
          {discounts.map((d) => (
            <div key={d.id} className="flex items-center justify-between bg-gray-700/50 rounded-lg p-3">
              <div>
                <span className="text-white font-mono">{d.code}</span>
                <span className="ml-3 text-gray-400 text-sm">
                  {d.discount_type === 'percent' ? `%${d.discount_value} indirim` : `$${d.discount_value} sabit fiyat`}
                </span>
              </div>
              <button
                onClick={() => deleteDiscount(d.id)}
                className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {discounts.length === 0 && (
            <p className="text-gray-500 text-center py-4">Henüz indirim kodu yok</p>
          )}
        </div>
      </div>
    </div>
  );
}
